import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import { BaseDraggableWidget } from '../Card';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';

import { getTickets } from '../../../api';
import SettingsContext from '../../../context/SettingsContext';

import { classNames, getUserFullName } from '../../helpers/functions';

[{ label: 'Today' }, { label: 'This week' }, { label: 'This month' }];

const TODAY = [moment().startOf('d').toDate(), moment().endOf('d').toDate()];

const THIS_WEEK = [
	moment().startOf('d').toDate(),
	moment().endOf('week').toDate(),
];

const THIS_MONTH = [
	moment().startOf('day').toDate(),
	moment().endOf('month').toDate(),
];

const THIRTY_DAYS = [
	moment().startOf('day').toDate(),
	moment().add(30, 'days').endOf('d').toDate(),
];

const OPTION_MAPPING = {
	today: TODAY,
	this_month: THIS_MONTH,
	this_week: THIS_WEEK,
	thirty_days: THIRTY_DAYS,
};

const dateKey = {
	SKIPPED: 'estimatedDate',
	ESTIMATED: 'estimatedDate',
	SCHEDULED: 'scheduledDate',
	COMPLETED: 'completedDate',
	RENEW: 'date',
};

const colorVariants = {
	critical: 'border-l-4 border-red-200',
	major: 'border-l-4 border-orange-200',
	minor: 'border-l-4 border-yellow-200/80',
};

const TicketsAll = ({ title, isPreview, ...rest }) => {
	const navigate = useNavigate();
	const { open: isAddTicketModalOpen, setOpen: openAddEventModal } =
		useModal('ticketAdd');

	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);
	const hasSettings = settings?.user?.widgetSettings
		? settings?.user?.widgetSettings[rest?.ctx?.data?.id]
				?.ticketFilterUserTicket
		: null;

	const [selectedTicket, setSelectedTicket] = useState(
		hasSettings || 'incoming'
	);
	const { setOpen: openTicketModal } = useModal('ticketOverview');

	const {
		value: tickets,
		execute: refreshTickets,
		error: hasError,
		status: isLoadingTickets,
	} = useAsync(getTickets, {
		defaultPayload: { status: [selectedTicket], pageSize: null },
	});

	useEffect(() => {
		refreshTickets({ status: [selectedTicket] });
	}, [selectedTicket]);

	const prevAddTicketModalOpen = usePrevious(isAddTicketModalOpen);
	useEffect(() => {
		if (!isAddTicketModalOpen && prevAddTicketModalOpen) {
			refreshTickets({ status: [selectedTicket] });
		}
	}, [isAddTicketModalOpen]);

	const handleUpdateSettings = (key, value) => {
		const widgetSettings = settings?.user?.widgetSettings;

		let updatedValue = {};

		if (widgetSettings) {
			if (widgetSettings[rest?.ctx?.data?.id]) {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: {
						...widgetSettings[rest?.ctx?.data?.id],
						[key]: value,
					},
				};
			} else {
				// settings object exists but not for this widget
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: { [key]: value },
				};
			}
		} else {
			// no settings object exists
			updatedValue = {
				[rest?.ctx?.data?.id]: { [key]: value },
			};
		}
		updateSettings(
			{
				type: 'user',
				key: 'widgetSettings',
				value: updatedValue,
			},
			{ showNotification: false }
		);
	};

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title="All Tickets"
				category="Tickets"
				description={'View tickets for this organization'}
				{...rest}
				isPreview={true}
			/>
		);
	}

	const isEmpty =
		tickets?.rows?.length === 0
			? { isEmpty: true, flexCenter: true, skipIcon: true }
			: {};
	const isError = !!hasError ? { isError: true, flexCenter: true } : {};

	return (
		<BaseDraggableWidget
			title="All Tickets"
			widgetProps={{
				isLoading: isLoadingTickets === 'pending',
				...isError,
				...isEmpty,
				isEmptyMessage: 'No assigned tickets',
				options: [
					{ label: 'Incoming', value: 'incoming' },
					{ label: 'In Progress', value: 'inprogress' },
					{ label: 'Done', value: 'done' },
					{ label: 'Closed', value: 'closed' },
				],
				optionSelected: selectedTicket,
				optionsOnChange: (index, value, raw) => {
					setSelectedTicket(raw?.value);
					handleUpdateSettings('ticketFilterUserTicket', raw?.value);
				},
				additonalMenuItems: [
					{
						name: 'Add new ticket',
						onClick: () => navigate('/tickets/create'),
					},
				],
			}}
			{...rest}
		>
			<div>
				{tickets?.rows?.length
					? tickets?.rows
							?.sort(
								(a, b) =>
									new Date(a[dateKey[a.status]]) -
									new Date(b[dateKey[b.status]])
							)
							?.map((ticket) => {
								return (
									<div
										className={classNames(
											'py-2 cursor-pointer border-l-4 pl-6 ',
											ticket?.status !== 'done' && ticket?.status !== 'closed'
												? colorVariants[ticket?.severity]
												: ''
										)}
										onClick={() => openTicketModal({ id: ticket?.id })}
									>
										<div>
											<span className="font-semibold">
												{ticket?.ticketCode}
											</span>
											{` ${ticket?.subject}`}
										</div>

										<div className="text-gray-400 text-xxs">
											{`${moment(ticket?.createdAt).fromNow()}`}
											{`${
												ticket?.createdBy
													? ` by ${getUserFullName(ticket?.createdBy)}`
													: ''
											}`}
										</div>
									</div>
								);
							})
					: []}
			</div>
		</BaseDraggableWidget>
	);
};

export default TicketsAll;
